// frontend/src/pages/PrivacyPolicyPage.tsx
import { InfoPageLayout } from './InfoPageLayout';

export function PrivacyPolicyPage() {
  return (
    <InfoPageLayout title="Privacy Policy" metaDescription="Llambada privacy policy — how we collect, use, and protect your data. GDPR-compliant. Your content is never used to train AI models without consent.">
      <p>Last Updated: February 3, 2026</p>

      <p>
        <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. ("we," "our," or "us") operates Llambada. This Privacy Policy explains how 
        we collect, use, disclose, and protect your information when you use our service.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li>Email address</li>
        <li>Display name (optional)</li>
        <li>Password (stored securely hashed)</li>
        <li>Country of residence (for tax and compliance purposes)</li>
      </ul>

      <h3>1.2 Payment Information</h3>
      <p>
        If you subscribe to a paid plan or participate in revenue sharing, we collect payment 
        information through our payment processor, Stripe. We do not store your full credit card 
        numbers on our servers. Stripe's privacy policy governs their handling of your payment data.
      </p>

      <h3>1.3 Mini-App Content</h3>
      <p>We store the content you create on our platform, including:</p>
      <ul>
        <li>Mini-App code (frontend and backend)</li>
        <li>Mini-App names and descriptions</li>
        <li>Conversation history with AI assistants used to create Mini-Apps</li>
        <li>Files you upload for processing by Mini-Apps</li>
      </ul>

      <h3>1.4 API Keys</h3>
      <p>
        If you provide your own API keys (such as Anthropic API keys), we store them encrypted. 
        These keys are used solely to make API calls on your behalf and are never shared with 
        third parties except the respective API provider.
      </p>

      <h3>1.5 Usage Data</h3>
      <p>We automatically collect certain information when you use the Service:</p>
      <ul>
        <li>IP address and approximate location</li>
        <li>Browser type and device information</li>
        <li>Pages visited and features used</li>
        <li>Mini-Apps accessed and usage patterns</li>
        <li>Error logs and performance data</li>
      </ul>

      <h3>1.6 Data Processed by Mini-Apps</h3>
      <p>
        When you use Mini-Apps (yours or others'), those Mini-Apps may process data you provide, 
        such as files you upload or information you enter. This data is processed on our servers 
        to provide the Mini-App functionality. We retain this data only as long as necessary to 
        provide the service, typically deleting it after processing unless the Mini-App is designed 
        to store data persistently.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Process your transactions and manage your account</li>
        <li>Generate AI-powered code based on your instructions</li>
        <li>Run and host your Mini-Apps</li>
        <li>Calculate and process revenue sharing payments</li>
        <li>Send you service-related communications</li>
        <li>Respond to your inquiries and provide support</li>
        <li>Detect and prevent fraud, abuse, and security incidents</li>
        <li>Comply with legal obligations</li>
        <li>Analyze usage patterns to improve our services</li>
      </ul>

      <h2>3. Information Sharing</h2>
      <p>We do not sell your personal information. We may share information in these circumstances:</p>

      <h3>3.1 Service Providers</h3>
      <p>
        We share information with third-party service providers who perform services on our behalf:
      </p>
      <ul>
        <li><strong>Supabase:</strong> Database and authentication infrastructure</li>
        <li><strong>Stripe:</strong> Payment processing and revenue sharing payouts</li>
        <li><strong>AI Providers (e.g., Anthropic):</strong> Code generation and AI features</li>
        <li><strong>Cloud Infrastructure:</strong> Hosting and running Mini-Apps</li>
      </ul>
      <p>
        These providers are contractually obligated to protect your information and use it only 
        for the services they provide to us.
      </p>

      <h3>3.2 Published Mini-Apps</h3>
      <p>
        If you publish a Mini-App, its name, description, and your display name (if provided) 
        become publicly visible. The Mini-App's code may be viewable by users who customize 
        (fork) your Mini-App.
      </p>

      <h3>3.3 Legal Requirements</h3>
      <p>
        We may disclose information if required by law, legal process, or government request, 
        or if we believe disclosure is necessary to protect our rights, your safety, or the 
        safety of others.
      </p>

      <h3>3.4 Business Transfers</h3>
      <p>
        If we are involved in a merger, acquisition, or sale of assets, your information may 
        be transferred as part of that transaction. We will notify you of any such change.
      </p>

      <h2>4. AI Processing</h2>
      <p>
        When you use AI features to create or modify Mini-Apps, your prompts and relevant context 
        are sent to AI providers (such as Anthropic). These providers process your data according 
        to their own privacy policies and terms. We select providers that commit to not using 
        customer data to train their models without explicit consent.
      </p>
      <p>
        We may use aggregated, anonymized data about how the Service is used to improve our 
        AI prompting and service quality, but we do not use your specific Mini-App code or 
        content to train AI models.
      </p>

      <h2>5. Data Security</h2>
      <p>
        We implement appropriate technical and organizational measures to protect your information, 
        including:
      </p>
      <ul>
        <li>Encryption of data in transit (TLS) and at rest</li>
        <li>Secure password hashing</li>
        <li>Encrypted storage of API keys</li>
        <li>Access controls and authentication</li>
        <li>Regular security assessments</li>
      </ul>
      <p>
        However, no method of transmission or storage is 100% secure. We cannot guarantee absolute 
        security of your information.
      </p>

      <h2>6. Data Retention</h2>
      <p>We retain your information as follows:</p>
      <ul>
        <li><strong>Account data:</strong> Until you delete your account, plus a reasonable period for backups</li>
        <li><strong>Mini-Apps:</strong> Until you delete them or your account</li>
        <li><strong>Transaction records:</strong> As required by tax and financial regulations (typically 7 years)</li>
        <li><strong>Usage logs:</strong> Generally 90 days, except where needed for security or legal purposes</li>
        <li><strong>Files processed by Mini-Apps:</strong> Typically deleted after processing unless persistent storage is enabled</li>
      </ul>

      <h2>7. Your Rights and Choices</h2>
      <p>Depending on your location, you may have certain rights regarding your personal information:</p>

      <h3>7.1 Access and Portability</h3>
      <p>
        You can access most of your data through your account dashboard. You may request a copy 
        of your personal data in a portable format.
      </p>

      <h3>7.2 Correction</h3>
      <p>
        You can update your account information through your profile settings. Contact us if you 
        need assistance correcting other information.
      </p>

      <h3>7.3 Deletion</h3>
      <p>
        You can delete your Mini-Apps at any time. You can request deletion of your account by 
        contacting us. Note that we may retain certain information as required by law or for 
        legitimate business purposes.
      </p>

      <h3>7.4 Marketing Communications</h3>
      <p>
        You can opt out of marketing emails by clicking the unsubscribe link in any marketing 
        email. You will continue to receive transactional emails related to your account.
      </p>

      <h3>7.5 Data Processing Objections</h3>
      <p>
        Where we process data based on legitimate interests, you may object to such processing. 
        Contact us to exercise this right.
      </p>

      <h2>8. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in countries other than your country 
        of residence, including Canada and the United States, where our service providers operate. 
        These countries may have different data protection laws. We ensure appropriate safeguards 
        are in place for such transfers.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is not intended for users under 18 years of age. We do not knowingly collect 
        personal information from children. If we learn we have collected information from a child, 
        we will delete it promptly.
      </p>

      <h2>10. Third-Party Mini-Apps</h2>
      <p>
        When you use Mini-Apps created by other users, those Mini-Apps may collect and process 
        data you provide. We are not responsible for the data practices of third-party Mini-Apps. 
        Review the description and behavior of Mini-Apps before providing sensitive information.
      </p>

      <h2>11. Cookies and Tracking</h2>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Maintain your login session</li>
        <li>Remember your preferences</li>
        <li>Analyze usage patterns</li>
        <li>Prevent fraud</li>
      </ul>
      <p>
        You can control cookies through your browser settings, but disabling cookies may affect 
        Service functionality.
      </p>

      <h2>12. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes 
        by email or through the Service. Your continued use after changes become effective constitutes 
        acceptance of the updated policy.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        For questions about this Privacy Policy or our data practices, please contact us at:
      </p>
      <p>
        <strong>Email:</strong> <a href="mailto:privacy@llambada.com">privacy@llambada.com</a>
      </p>
      <p>
        <strong>Mail:</strong><br />
        <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>.<br />
        Privacy Inquiries<br />
        410-117 Quai St-André<br />
        Québec, QC<br />
        Canada
      </p>

      <h2>14. Additional Rights for Certain Jurisdictions</h2>

      <h3>14.1 European Economic Area (EEA) and UK</h3>
      <p>
        If you are in the EEA or UK, you have additional rights under GDPR including the right to 
        lodge a complaint with a supervisory authority. Our legal basis for processing includes 
        contract performance, legitimate interests, and consent where applicable.
      </p>

      <h3>14.2 California Residents</h3>
      <p>
        California residents have additional rights under the CCPA, including the right to know 
        what personal information is collected and the right to request deletion. We do not sell 
        personal information as defined by the CCPA.
      </p>

      <h3>14.3 Canadian Residents</h3>
      <p>
        We comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) 
        and applicable provincial privacy laws. You have the right to access and correct your 
        personal information and to withdraw consent to certain uses.
      </p>
    </InfoPageLayout>
  );
}
